import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Search, Filter, Clock, CheckCircle, XCircle, AlertCircle, Calendar, MessageSquare, Image as ImageIcon, FileText, Edit, HomeIcon, PlusCircle, Pencil, DollarSign, UploadCloud, Plus } from 'lucide-react';
type RequestStatus = 'pending' | 'in-progress' | 'approved' | 'rejected';
type RequestType = 'new-model' | 'new-option' | 'image-update' | 'details-update' | 'remove-option' | 'update-model' | 'pricing-update' | 'general';
type Request = {
  id: string;
  itemName: string;
  community: string;
  category: string;
  type: RequestType;
  status: RequestStatus;
  description: string;
  submittedDate: string;
  updatedDate: string;
  adminNotes?: string;
};
const requests: Request[] = [{
  id: 'REQ-001',
  itemName: 'Black Stainless Fridge',
  community: 'Oakwood Estates',
  category: 'Interior > Kitchen > Refrigerators',
  type: 'image-update',
  status: 'approved',
  description: 'Need updated product image with new model',
  submittedDate: '2024-01-20',
  updatedDate: '2024-01-22',
  adminNotes: 'Image updated successfully. New photo from manufacturer.'
}, {
  id: 'REQ-002',
  itemName: 'Gray Asphalt Shingles',
  community: 'Maple Residences',
  category: 'Exterior > Roofing > Shingles',
  type: 'details-update',
  status: 'in-progress',
  description: 'Update warranty information from 25 to 30 years',
  submittedDate: '2024-01-21',
  updatedDate: '2024-01-21'
}, {
  id: 'REQ-003',
  itemName: 'White Vinyl Siding',
  community: 'Riverside Commons',
  category: 'Exterior > Siding > Vinyl',
  type: 'remove-option',
  status: 'pending',
  description: 'Discontinued by manufacturer, need to remove from options',
  submittedDate: '2024-01-22',
  updatedDate: '2024-01-22'
}, {
  id: 'REQ-004',
  itemName: 'Natural Oak Flooring',
  community: 'Oakwood Estates',
  category: 'Interior > Flooring > Hardwood',
  type: 'pricing-update',
  status: 'rejected',
  description: 'Change price from $8,500 to $9,200',
  submittedDate: '2024-01-19',
  updatedDate: '2024-01-20',
  adminNotes: 'Price increase not approved. Please provide manufacturer documentation.'
}, {
  id: 'REQ-005',
  itemName: 'The Lawson',
  community: 'Maple Residences',
  category: 'Models',
  type: 'new-model',
  status: 'in-progress',
  description: 'Add new Lawson model to the community with 3 elevations',
  submittedDate: '2024-01-22',
  updatedDate: '2024-01-22'
}, {
  id: 'REQ-006',
  itemName: 'Smart Home Package',
  community: 'All Communities',
  category: 'Packages',
  type: 'new-option',
  status: 'pending',
  description: 'Create a new smart home bundle including thermostat, doorbell, and locks',
  submittedDate: '2024-01-23',
  updatedDate: '2024-01-23'
}, {
  id: 'REQ-007',
  itemName: 'General Inquiry',
  community: 'Riverside Commons',
  category: 'General',
  type: 'general',
  status: 'pending',
  description: 'Can we get a training session for our new sales agents next week?',
  submittedDate: '2024-01-24',
  updatedDate: '2024-01-24'
}];
const statusConfig = {
  pending: {
    label: 'Pending',
    variant: 'warning' as const,
    icon: Clock,
    color: 'text-amber-600'
  },
  'in-progress': {
    label: 'In Progress',
    variant: 'primary' as const,
    icon: AlertCircle,
    color: 'text-blue-600'
  },
  approved: {
    label: 'Completed',
    variant: 'success' as const,
    icon: CheckCircle,
    color: 'text-green-600'
  },
  rejected: {
    label: 'Rejected',
    variant: 'danger' as const,
    icon: XCircle,
    color: 'text-red-600'
  }
};
const typeConfig = {
  'new-model': {
    label: 'Add New Model',
    icon: HomeIcon,
    color: 'text-teal-600'
  },
  'new-option': {
    label: 'Add New Option',
    icon: PlusCircle,
    color: 'text-indigo-600'
  },
  'image-update': {
    label: 'Image Update',
    icon: ImageIcon,
    color: 'text-purple-600'
  },
  'details-update': {
    label: 'Details Update',
    icon: Edit,
    color: 'text-blue-600'
  },
  'remove-option': {
    label: 'Remove Option',
    icon: XCircle,
    color: 'text-red-600'
  },
  'update-model': {
    label: 'Update Model Details',
    icon: Pencil,
    color: 'text-orange-600'
  },
  'pricing-update': {
    label: 'Pricing Update',
    icon: DollarSign,
    color: 'text-green-600'
  },
  general: {
    label: 'General Request',
    icon: MessageSquare,
    color: 'text-slate-600'
  }
};
export function RequestsPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RequestStatus>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | RequestType>('all');
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
  const [isNewRequestModalOpen, setIsNewRequestModalOpen] = useState(false);
  const [newRequestData, setNewRequestData] = useState({
    type: 'new-model' as RequestType,
    community: '',
    itemName: '',
    description: '',
    priority: 'medium'
  });
  // Filtered requests
  const filteredRequests = useMemo(() => {
    let filtered = requests;
    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(r => r.status === statusFilter);
    }
    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(r => r.type === typeFilter);
    }
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r => r.id.toLowerCase().includes(query) || r.itemName.toLowerCase().includes(query) || r.community.toLowerCase().includes(query) || r.category.toLowerCase().includes(query) || r.description.toLowerCase().includes(query));
    }
    return filtered;
  }, [statusFilter, typeFilter, searchQuery]);
  // Stats
  const stats = useMemo(() => {
    return {
      total: requests.length,
      pending: requests.filter(r => r.status === 'pending').length,
      inProgress: requests.filter(r => r.status === 'in-progress').length,
      approved: requests.filter(r => r.status === 'approved').length,
      rejected: requests.filter(r => r.status === 'rejected').length
    };
  }, []);
  // Sticky CTA logic
  const bannerRef = useRef<HTMLButtonElement>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => setShowStickyCta(!entry.isIntersecting), {
      threshold: 0
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return <>
      {/* Sticky CTA - appears when banner scrolls out of view */}
      <div className={`fixed top-0 left-64 right-0 z-30 transition-all duration-300 ${showStickyCta ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}`} data-id="element-6585">
        <button onClick={() => setIsNewRequestModalOpen(true)} className="w-full flex items-center justify-center gap-3 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 transition-colors shadow-lg" data-id="element-6586">
          <Plus className="h-5 w-5" data-id="element-6587" />
          <span className="font-semibold" data-id="element-6588">Make a New Change Request</span>
        </button>
      </div>

      <div className="space-y-6 max-w-7xl mx-auto" data-id="element-6589">
        <div className="flex items-center justify-between" data-id="element-6590">
          <div data-id="element-6591">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-6592">
              Change Requests
            </h1>
            <p className="text-slate-500" data-id="element-6593">
              Track your submitted change requests
            </p>
          </div>
        </div>

        {/* Prominent CTA Banner */}
        <button ref={bannerRef} onClick={() => setIsNewRequestModalOpen(true)} className="w-full flex items-center justify-between bg-primary-600 hover:bg-primary-700 text-white rounded-xl px-6 py-5 transition-colors group shadow-sm" data-id="element-6594">
          <div className="flex items-center gap-4" data-id="element-6595">
            <div className="bg-white/20 rounded-lg p-2.5" data-id="element-6596">
              <Plus className="h-6 w-6" data-id="element-6597" />
            </div>
            <div className="text-left" data-id="element-6598">
              <p className="text-lg font-semibold" data-id="element-6599">Make a New Change Request</p>
              <p className="text-sm text-white/70" data-id="element-6600">
                Submit a request to the ResVR team — new models, options,
                updates, and more
              </p>
            </div>
          </div>
          <div className="bg-white/20 rounded-full p-2 group-hover:bg-white/30 transition-colors" data-id="element-6601">
            <Plus className="h-5 w-5" data-id="element-6602" />
          </div>
        </button>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4" data-id="element-6603">
          <Card noPadding data-id="element-6604">
            <div className="p-4" data-id="element-6605">
              <div className="flex items-center justify-between mb-2" data-id="element-6606">
                <p className="text-sm text-slate-500" data-id="element-6607">Total Requests</p>
                <FileText className="h-4 w-4 text-slate-400" data-id="element-6608" />
              </div>
              <p className="text-2xl font-bold text-slate-900" data-id="element-6609">{stats.total}</p>
            </div>
          </Card>

          <Card noPadding data-id="element-6610">
            <div className="p-4" data-id="element-6611">
              <div className="flex items-center justify-between mb-2" data-id="element-6612">
                <p className="text-sm text-slate-500" data-id="element-6613">Pending</p>
                <Clock className="h-4 w-4 text-amber-500" data-id="element-6614" />
              </div>
              <p className="text-2xl font-bold text-amber-600" data-id="element-6615">
                {stats.pending}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-6616">
            <div className="p-4" data-id="element-6617">
              <div className="flex items-center justify-between mb-2" data-id="element-6618">
                <p className="text-sm text-slate-500" data-id="element-6619">In Progress</p>
                <AlertCircle className="h-4 w-4 text-blue-500" data-id="element-6620" />
              </div>
              <p className="text-2xl font-bold text-blue-600" data-id="element-6621">
                {stats.inProgress}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-6622">
            <div className="p-4" data-id="element-6623">
              <div className="flex items-center justify-between mb-2" data-id="element-6624">
                <p className="text-sm text-slate-500" data-id="element-6625">Completed</p>
                <CheckCircle className="h-4 w-4 text-green-500" data-id="element-6626" />
              </div>
              <p className="text-2xl font-bold text-green-600" data-id="element-6627">
                {stats.approved}
              </p>
            </div>
          </Card>

          <Card noPadding data-id="element-6628">
            <div className="p-4" data-id="element-6629">
              <div className="flex items-center justify-between mb-2" data-id="element-6630">
                <p className="text-sm text-slate-500" data-id="element-6631">Rejected</p>
                <XCircle className="h-4 w-4 text-red-500" data-id="element-6632" />
              </div>
              <p className="text-2xl font-bold text-red-600" data-id="element-6633">
                {stats.rejected}
              </p>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card noPadding data-id="element-6634">
          <div className="p-4 border-b border-slate-200 bg-slate-50 rounded-t-xl" data-id="element-6635">
            <div className="flex items-center gap-3" data-id="element-6636">
              {/* Search */}
              <div className="flex-1 relative" data-id="element-6637">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-6638" />
                <input type="text" placeholder="Search by ID, item name, community, or description..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6639" />
              </div>

              {/* Status Filter */}
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-6640">
                <option value="all" data-id="element-6641">All Status</option>
                <option value="pending" data-id="element-6642">Pending</option>
                <option value="in-progress" data-id="element-6643">In Progress</option>
                <option value="approved" data-id="element-6644">Completed</option>
                <option value="rejected" data-id="element-6645">Rejected</option>
              </select>

              {/* Type Filter */}
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as any)} className="px-4 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-6646">
                <option value="all" data-id="element-6647">All Types</option>
                {(Object.keys(typeConfig) as RequestType[]).map(type => <option key={type} value={type} data-id="element-6648">
                    {typeConfig[type].label}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-6649">
            <table className="w-full text-left text-sm" data-id="element-6650">
              <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-6651">
                <tr data-id="element-6652">
                  <th className="px-6 py-4" data-id="element-6653">Request ID</th>
                  <th className="px-6 py-4" data-id="element-6654">Community</th>
                  <th className="px-6 py-4" data-id="element-6655">Item</th>
                  <th className="px-6 py-4" data-id="element-6656">Type</th>
                  <th className="px-6 py-4" data-id="element-6657">Status</th>
                  <th className="px-6 py-4" data-id="element-6658">Submitted</th>
                  <th className="px-6 py-4 text-right" data-id="element-6659">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-6660">
                {filteredRequests.length === 0 ? <tr data-id="element-6661">
                    <td colSpan={7} className="px-6 py-12 text-center text-slate-500" data-id="element-6662">
                      No requests found matching your filters
                    </td>
                  </tr> : filteredRequests.map(request => {
                const StatusIcon = statusConfig[request.status].icon;
                const TypeIcon = typeConfig[request.type].icon;
                return <tr key={request.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => setSelectedRequest(request)} data-id="element-6663">
                        <td className="px-6 py-4 font-medium text-slate-900" data-id="element-6664">
                          {request.id}
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-6665">
                          {request.community}
                        </td>
                        <td className="px-6 py-4" data-id="element-6666">
                          <div className="font-medium text-slate-900" data-id="element-6667">
                            {request.itemName}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-6668">
                            {request.category}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-6669">
                          <div className="flex items-center gap-2" data-id="element-6670">
                            <TypeIcon className={`h-4 w-4 ${typeConfig[request.type].color}`} data-id="element-6671" />
                            <span className="text-slate-700" data-id="element-6672">
                              {typeConfig[request.type].label}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-6673">
                          <Badge variant={statusConfig[request.status].variant} data-id="element-6674">
                            <div className="flex items-center gap-1" data-id="element-6675">
                              <StatusIcon className="h-3 w-3" data-id="element-6676" />
                              {statusConfig[request.status].label}
                            </div>
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-6677">
                          <div className="flex items-center gap-1" data-id="element-6678">
                            <Calendar className="h-3 w-3 text-slate-400" data-id="element-6679" />
                            {new Date(request.submittedDate).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-6680">
                          <Button variant="ghost" size="sm" data-id="element-6681">
                            View Details
                          </Button>
                        </td>
                      </tr>;
              })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* New Request Modal */}
      {isNewRequestModalOpen && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-6682">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-6683">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10" data-id="element-6684">
              <div data-id="element-6685">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-6686">
                  New Change Request
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-6687">
                  Submit a request to the ResVR team
                </p>
              </div>
              <button onClick={() => setIsNewRequestModalOpen(false)} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-6688">
                <XCircle className="h-5 w-5" data-id="element-6689" />
              </button>
            </div>

            <div className="p-6 space-y-6" data-id="element-6690">
              {/* Request Type */}
              <div data-id="element-6691">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6692">
                  Request Type
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-6693">
                  {(Object.keys(typeConfig) as RequestType[]).map(type => {
                const config = typeConfig[type];
                const Icon = config.icon;
                const isSelected = newRequestData.type === type;
                return <button key={type} onClick={() => setNewRequestData({
                  ...newRequestData,
                  type
                })} className={`flex flex-col items-center justify-center p-3 rounded-lg border text-center transition-all ${isSelected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500' : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-6694">
                        <Icon className={`h-5 w-5 mb-2 ${isSelected ? 'text-primary-600' : config.color}`} data-id="element-6695" />
                        <span className={`text-xs font-medium ${isSelected ? 'text-primary-900' : 'text-slate-700'}`} data-id="element-6696">
                          {config.label}
                        </span>
                      </button>;
              })}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-6697">
                {/* Community */}
                <div data-id="element-6698">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6699">
                    Community
                  </label>
                  <select value={newRequestData.community} onChange={e => setNewRequestData({
                ...newRequestData,
                community: e.target.value
              })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-6700">
                    <option value="" data-id="element-6701">Select a community...</option>
                    <option value="Oakwood Estates" data-id="element-6702">Oakwood Estates</option>
                    <option value="Maple Residences" data-id="element-6703">Maple Residences</option>
                    <option value="Riverside Commons" data-id="element-6704">Riverside Commons</option>
                    <option value="All Communities" data-id="element-6705">All Communities</option>
                  </select>
                </div>

                {/* Priority */}
                <div data-id="element-6706">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6707">
                    Priority
                  </label>
                  <select value={newRequestData.priority} onChange={e => setNewRequestData({
                ...newRequestData,
                priority: e.target.value
              })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-6708">
                    <option value="low" data-id="element-6709">Low</option>
                    <option value="medium" data-id="element-6710">Medium</option>
                    <option value="high" data-id="element-6711">High</option>
                  </select>
                </div>
              </div>

              {/* Item Name */}
              <div data-id="element-6712">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6713">
                  {newRequestData.type.includes('model') ? 'Model Name' : newRequestData.type === 'general' ? 'Subject' : 'Option Name'}
                </label>
                {newRequestData.type === 'update-model' ? <select value={newRequestData.itemName} onChange={e => setNewRequestData({
              ...newRequestData,
              itemName: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-6714">
                    <option value="" data-id="element-6715">Select a model...</option>
                    <option value="The Lawson" data-id="element-6716">The Lawson</option>
                    <option value="The Samson" data-id="element-6717">The Samson</option>
                    <option value="The Pine" data-id="element-6718">The Pine</option>
                    <option value="The Oakridge" data-id="element-6719">The Oakridge</option>
                    <option value="The Maple" data-id="element-6720">The Maple</option>
                  </select> : <input type="text" value={newRequestData.itemName} onChange={e => setNewRequestData({
              ...newRequestData,
              itemName: e.target.value
            })} placeholder={`${newRequestData.type === 'new-model' ? 'Enter new model name' : newRequestData.type === 'general' ? 'Enter subject' : 'Enter option name'}...`} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-6721" />}
              </div>

              {/* Description */}
              <div data-id="element-6722">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6723">
                  Description
                </label>
                <textarea value={newRequestData.description} onChange={e => setNewRequestData({
              ...newRequestData,
              description: e.target.value
            })} placeholder="Provide detailed information about your request..." rows={4} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-6724" />
              </div>

              {/* Attachments */}
              <div data-id="element-6725">
                <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-6726">
                  Attachments
                </label>
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer" data-id="element-6727">
                  <UploadCloud className="h-8 w-8 text-slate-400 mb-2" data-id="element-6728" />
                  <p className="text-sm font-medium text-slate-700" data-id="element-6729">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-slate-500 mt-1" data-id="element-6730">
                    SVG, PNG, JPG, PDF or ZIP (max. 10MB)
                  </p>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3 bg-slate-50 rounded-b-lg" data-id="element-6731">
              <Button variant="outline" onClick={() => setIsNewRequestModalOpen(false)} data-id="element-6732">
                Cancel
              </Button>
              <Button onClick={() => setIsNewRequestModalOpen(false)} data-id="element-6733">
                Submit Request
              </Button>
            </div>
          </div>
        </div>}

      {/* Request Details Modal */}
      {selectedRequest && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-6734">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-6735">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white" data-id="element-6736">
              <div data-id="element-6737">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-6738">
                  Request Details
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-6739">
                  {selectedRequest.id}
                </p>
              </div>
              <button onClick={() => setSelectedRequest(null)} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-6740">
                <XCircle className="h-5 w-5" data-id="element-6741" />
              </button>
            </div>

            <div className="p-6 space-y-6" data-id="element-6742">
              {/* Status & Type */}
              <div className="flex items-center gap-3" data-id="element-6743">
                <Badge variant={statusConfig[selectedRequest.status].variant} className="text-sm" data-id="element-6744">
                  <div className="flex items-center gap-1" data-id="element-6745">
                    {createElement(statusConfig[selectedRequest.status].icon, {
                  className: 'h-3 w-3'
                })}
                    {statusConfig[selectedRequest.status].label}
                  </div>
                </Badge>
                <Badge variant="neutral" className="text-sm" data-id="element-6746">
                  <div className="flex items-center gap-1" data-id="element-6747">
                    {createElement(typeConfig[selectedRequest.type].icon, {
                  className: 'h-3 w-3'
                })}
                    {typeConfig[selectedRequest.type].label}
                  </div>
                </Badge>
              </div>

              {/* Option Info */}
              <div className="bg-slate-50 rounded-lg p-4 space-y-3" data-id="element-6748">
                <div className="grid grid-cols-2 gap-4" data-id="element-6749">
                  <div data-id="element-6750">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-6751">Community</p>
                    <p className="font-medium text-slate-900" data-id="element-6752">
                      {selectedRequest.community}
                    </p>
                  </div>
                  <div data-id="element-6753">
                    <p className="text-xs text-slate-500 mb-1" data-id="element-6754">Item Name</p>
                    <p className="font-medium text-slate-900" data-id="element-6755">
                      {selectedRequest.itemName}
                    </p>
                  </div>
                </div>
                <div data-id="element-6756">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-6757">Category Path</p>
                  <p className="text-sm text-slate-700" data-id="element-6758">
                    {selectedRequest.category}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div data-id="element-6759">
                <p className="text-sm font-medium text-slate-700 mb-2" data-id="element-6760">
                  Description
                </p>
                <div className="bg-slate-50 rounded-lg p-4" data-id="element-6761">
                  <p className="text-slate-700" data-id="element-6762">
                    {selectedRequest.description}
                  </p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4" data-id="element-6763">
                <div data-id="element-6764">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-6765">Submitted Date</p>
                  <div className="flex items-center gap-2 text-slate-700" data-id="element-6766">
                    <Calendar className="h-4 w-4 text-slate-400" data-id="element-6767" />
                    {new Date(selectedRequest.submittedDate).toLocaleDateString()}
                  </div>
                </div>
                <div data-id="element-6768">
                  <p className="text-xs text-slate-500 mb-1" data-id="element-6769">Last Updated</p>
                  <div className="flex items-center gap-2 text-slate-700" data-id="element-6770">
                    <Clock className="h-4 w-4 text-slate-400" data-id="element-6771" />
                    {new Date(selectedRequest.updatedDate).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Admin Notes */}
              {selectedRequest.adminNotes && <div data-id="element-6772">
                  <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2" data-id="element-6773">
                    <MessageSquare className="h-4 w-4" data-id="element-6774" />
                    Admin Notes
                  </p>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-6775">
                    <p className="text-sm text-blue-900" data-id="element-6776">
                      {selectedRequest.adminNotes}
                    </p>
                  </div>
                </div>}
            </div>

            <div className="p-6 border-t border-slate-200 flex justify-end" data-id="element-6777">
              <Button onClick={() => setSelectedRequest(null)} data-id="element-6778">Close</Button>
            </div>
          </div>
        </div>}
    </>;
}